import { mapTargetCTA, mapTargetLink, onDatalayer, onDataLayerCTA } from '@/App.datalayer';
import { MuiMenu } from '@/features/shared/material-ui';
import clsx from 'clsx';
import React, { BaseSyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import useCurrentLocation from '../../hooks/useCurrentLocation';
import { NavigationDropdownProps, SubNavigationCustomRef } from '../../PortalNavigation.interfaces';

import { withNavigationDropdownDivider } from '../../variants/dropdown/NavigationDropdownDivider';
import MenuItem from '@/features/PortalNavigation/variants/dropdown/MenuItem/MenuItem';
import { useNavigationDropdownStyles } from '@/features/PortalNavigation/components/NavigationDropdown/NavigationDropdown.styles';
import { withGTMPipe } from '@/features/shared/HOCs/withGTM';

const NavigationDropdownItemCTA = withGTMPipe(
  {
    onDatalayer,
    config: { mapTarget: mapTargetLink },
  },
  {
    onDatalayer: onDataLayerCTA,
    config: { mapTarget: mapTargetCTA },
  },
)(MenuItem);

const NavigationDropdownItemWithDivider = withNavigationDropdownDivider(NavigationDropdownItemCTA);

const getNavigationContentWidth = (
  elementRef: HTMLDivElement | null,
  subMenuPosition: 'left' | 'right' | 'center',
): number => {
  if (!elementRef) return 0;
  const navigationContentElement = elementRef?.querySelector(
    '.navigation-dropdown-content',
  ) as HTMLElement;

  if (subMenuPosition === 'right') {
    return (navigationContentElement?.offsetWidth - 5) * -1;
  }

  if (subMenuPosition === 'center') {
    return navigationContentElement?.offsetWidth;
  }

  return navigationContentElement?.offsetWidth + 25;
};

const NavigationDropdown = ({
  navigationItems = [],
  triggerButtonRef,
  onMenuClose,
  onNavItemClick,
  'data-datalayer-position': datalayerPosition,
  'data-datalayer-variant': datalayerVariant,
  horizontalAlignment = 'left',
  dropdownDistance = 20,
}: NavigationDropdownProps) => {
  const classes = useNavigationDropdownStyles();
  const desktopDropdownRef = useRef<HTMLDivElement | null>(null);
  const [subNavOpened, setSubNavOpened] = useState<SubNavigationCustomRef | null>(null);
  const { hasChildrenWithCurrentLocation, isCurrentLocation } = useCurrentLocation();
  const [subMenuPosition, setSubMenuPosition] = useState<'left' | 'right'>('right');

  const actionButtonHeight = useMemo(
    () => (triggerButtonRef?.clientHeight ?? 0) + dropdownDistance,
    [triggerButtonRef, dropdownDistance],
  );

  const muiMenuCommonsProps = {
    disableScrollLock: true,
    PopoverClasses: {
      root: classes.menuPopover,
    },
  };

  const handleOpenSubMenuClick =
    (navigationIndex: number) => (event: BaseSyntheticEvent<any, any, any> | undefined) => {
      const isSbMenuOpen = subNavOpened?.parentIndex === navigationIndex;
      setSubNavOpened(
        isSbMenuOpen
          ? null
          : {
              parentIndex: navigationIndex,
              parentAnchorEl: event?.currentTarget,
            },
      );
    };

  const getSubMenuRefByIndex = (parentIndex: number) =>
    subNavOpened && subNavOpened.parentIndex === parentIndex
      ? subNavOpened.parentAnchorEl
      : undefined;

  const handleCloseMainMenu = () => {
    setSubNavOpened(null);
    onMenuClose();
  };

  const handleNavigationItemClick = () => {
    setSubNavOpened(null);
    onNavItemClick();
  };

  useEffect(() => {
    if (!triggerButtonRef) {
      setSubNavOpened(null);
    }

    if (triggerButtonRef) {
      const windowWidth = document.getElementsByTagName('body')[0].clientWidth;
      const subMenuBound: any = triggerButtonRef?.getBoundingClientRect() || {};
      const subMenuX = subMenuBound?.x;
      const subMenuWidth = 240 * 2;
      setSubMenuPosition(subMenuX + subMenuWidth > windowWidth ? 'left' : 'right');
    }
  }, [triggerButtonRef, setSubMenuPosition]);

  return (
    <MuiMenu
      ref={desktopDropdownRef}
      open={Boolean(triggerButtonRef)}
      anchorEl={triggerButtonRef}
      onClose={handleCloseMainMenu}
      MenuListProps={{
        className: clsx(classes.navigationDropdownContent, 'navigation-dropdown-content'),
      }}
      transformOrigin={{ horizontal: horizontalAlignment, vertical: 'top' }}
      anchorOrigin={{ horizontal: horizontalAlignment, vertical: actionButtonHeight }}
      {...muiMenuCommonsProps}
      getContentAnchorEl={null}
      data-datalayer-position={datalayerPosition}
      data-datalayer-variant={datalayerVariant}
    >
      {navigationItems?.map((parentItem, navParentIndex) => (
        <NavigationDropdownItemWithDivider
          key={navParentIndex.toString()}
          navigationItem={parentItem}
          onItemClick={
            parentItem?.navigationItems
              ? handleOpenSubMenuClick(navParentIndex)
              : handleNavigationItemClick
          }
          isActive={
            navParentIndex === subNavOpened?.parentIndex ||
            isCurrentLocation(parentItem) ||
            hasChildrenWithCurrentLocation(parentItem?.navigationItems)
          }
        >
          {parentItem?.navigationItems && (
            <MuiMenu
              open={Boolean(getSubMenuRefByIndex(navParentIndex))}
              anchorEl={getSubMenuRefByIndex(navParentIndex)}
              onClose={() => setSubNavOpened(null)}
              transformOrigin={{
                vertical: -15,
                horizontal: getNavigationContentWidth(desktopDropdownRef?.current, subMenuPosition),
              }}
              MenuListProps={{
                className: clsx(classes.navigationDropdownContent),
              }}
              {...muiMenuCommonsProps}
              style={{ pointerEvents: 'none' }}
            >
              <div style={{ pointerEvents: 'auto' }}>
                {(parentItem?.navigationItems || []).map((childItem, navChildIndex) => (
                  <NavigationDropdownItemWithDivider
                    key={navChildIndex.toString()}
                    navigationItem={childItem}
                    onItemClick={handleNavigationItemClick}
                    isActive={isCurrentLocation(childItem)}
                  />
                ))}
              </div>
            </MuiMenu>
          )}
        </NavigationDropdownItemWithDivider>
      ))}
    </MuiMenu>
  );
};

export default NavigationDropdown;
